import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, Plus, ShoppingCart } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import { apiError, fmtDate, fmtMoney, type IndustryTenant, type Supplier } from '../lib/industria'
import {
  PURCHASE_STATUS_BADGE, PURCHASE_STATUS_LABEL,
  type NeedRow, type PurchaseRow, type PurchaseStatus,
} from '../lib/compras'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'
const STATUSES: PurchaseStatus[] = ['draft', 'ordered', 'partial', 'received', 'cancelled']

type Line = { materialId: string; code: string; name: string; qty: string; unitCost: string }

export function IndustriaComprasPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-compras', 'edit')

  const [status, setStatus] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    supplierId: '', tenantId: '', entryMode: 'compra_ws' as 'compra_ws' | 'compra_por_conta',
    expectedAt: '', notes: '',
  })
  const [lines, setLines] = useState<Line[]>([])

  const { data: needs = [], isLoading: loadingNeeds } = useQuery<NeedRow[]>({
    queryKey: ['compras', 'needs'],
    queryFn: async () => (await api.get('/purchase-needs')).data,
  })

  const { data: purchases = [] } = useQuery<PurchaseRow[]>({
    queryKey: ['compras', 'purchases', status],
    queryFn: async () => (await api.get('/material-purchases', {
      params: { status: status || undefined },
    })).data,
  })

  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['industria', 'suppliers'],
    queryFn: async () => (await api.get('/suppliers')).data,
  })

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/material-purchases', {
      supplierId: form.supplierId,
      tenantId: form.entryMode === 'compra_por_conta' ? form.tenantId : null,
      entryMode: form.entryMode,
      expectedAt: form.expectedAt || null,
      notes: form.notes.trim() || null,
      items: lines.filter(l => Number(l.qty) > 0).map(l => ({
        materialId: l.materialId,
        qtyOrdered: Number(l.qty),
        unitCost: l.unitCost ? Number(l.unitCost) : null,
      })),
    })).data as { id: string; number: string },
    onSuccess: (row) => {
      queryClient.invalidateQueries({ queryKey: ['compras'] })
      setOpen(false)
      setSelected(new Set())
      navigate(`/industria/compras/${row.id}`)
    },
    onError: (e) => setError(apiError(e, 'Não foi possível criar o pedido')),
  })

  const late = needs.filter(n => n.late).length
  const key = (n: NeedRow) => `${n.materialId}|${n.ownerTenantId ?? 'ws'}`

  const selectedNeeds = useMemo(
    () => needs.filter(n => selected.has(key(n))),
    [needs, selected],
  )

  // Um pedido é de um fornecedor só: a seleção sugere o dono e o fornecedor mais
  // comum, mas quem decide é quem compra.
  function startPurchase() {
    const first = selectedNeeds[0]
    const sameOwner = selectedNeeds.every(n => n.ownerTenantId === first?.ownerTenantId)
    setForm({
      supplierId: first?.supplierId ?? '',
      tenantId: sameOwner ? (first?.ownerTenantId ?? '') : '',
      entryMode: sameOwner && first?.ownerTenantId ? 'compra_por_conta' : 'compra_ws',
      expectedAt: '',
      notes: selectedNeeds.flatMap(n => n.orders).length
        ? `Demanda de: ${[...new Set(selectedNeeds.flatMap(n => n.orders))].join(', ')}`
        : '',
    })
    setLines(selectedNeeds.map(n => ({
      materialId: n.materialId,
      code: n.materialCode,
      name: n.materialName,
      qty: String(n.need),
      unitCost: n.avgCost ? String(Number(n.avgCost)) : '',
    })))
    setError(null)
    setOpen(true)
  }

  const total = lines.reduce((a, l) => a + Number(l.qty || 0) * Number(l.unitCost || 0), 0)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Compras</h2>
        <p className="text-sm text-gray-500">
          Necessidade líquida: demanda das ordens − saldo do dono − o que já está pedido
        </p>
      </div>

      {late > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {late} item(ns) já passaram da data de comprar — o lead time do fornecedor não fecha mais
          com a ordem programada.
        </div>
      )}

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">
            Necessidade líquida
            {needs.length > 0 && <span className="ml-2 font-normal text-gray-400">{needs.length} item(ns)</span>}
          </span>
          {canEdit && selected.size > 0 && (
            <Button size="sm" onClick={startPurchase}>
              <ShoppingCart className="h-4 w-4" /> Gerar pedido ({selected.size})
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loadingNeeds ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Calculando...</p>
          ) : needs.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">
              Nada a comprar — nenhuma ordem planejada puxa material que falte, e nenhum item está
              abaixo do mínimo
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="w-10 px-4 py-2.5"></th>
                    <th className="px-4 py-2.5 text-left font-medium">Material</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-right font-medium">Demanda</th>
                    <th className="px-4 py-2.5 text-right font-medium">Saldo</th>
                    <th className="px-4 py-2.5 text-right font-medium">Já pedido</th>
                    <th className="px-4 py-2.5 text-right font-medium">Comprar</th>
                    <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                    <th className="px-4 py-2.5 text-left font-medium">Comprar até</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {needs.map(n => (
                    <tr key={key(n)} className={selected.has(key(n)) ? 'bg-blue-50/50' : 'hover:bg-gray-50/60'}>
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300"
                          checked={selected.has(key(n))}
                          onChange={() => setSelected(s => {
                            const next = new Set(s)
                            next.has(key(n)) ? next.delete(key(n)) : next.add(key(n))
                            return next
                          })}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{n.materialName}</span>
                        <span className="block font-mono text-xs text-gray-400">
                          {n.materialCode}
                          {n.orders.length > 0 && ` · ${n.orders.slice(0, 2).join(', ')}${n.orders.length > 2 ? '…' : ''}`}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{n.ownerName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">
                        {n.demand.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {n.balance.toLocaleString('pt-BR')}
                        {n.minQty > 0 && n.balance < n.minQty && (
                          <span className="block text-xs text-amber-600">mín. {n.minQty}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                        {n.onOrder ? n.onOrder.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-900">
                        {n.need.toLocaleString('pt-BR')} <span className="text-xs font-normal text-gray-400">{n.unit}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {n.supplierName ?? <span className="text-amber-600">sem fornecedor</span>}
                        {n.leadTimeDays != null && (
                          <span className="block text-xs text-gray-400">{n.leadTimeDays} dias</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {n.buyBy ? (
                          <span className={n.late ? 'font-semibold text-red-600' : 'text-gray-700'}>
                            {fmtDate(n.buyBy)}
                            {n.neededAt && (
                              <span className="block text-xs font-normal text-gray-400">
                                OP em {fmtDate(n.neededAt)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Pedidos</span>
          <div className="flex items-center gap-2">
            <select className="h-9 rounded-lg border border-gray-300 px-2 text-sm" value={status} onChange={e => setStatus(e.target.value)}>
              <option value="">Todos</option>
              {STATUSES.map(s => <option key={s} value={s}>{PURCHASE_STATUS_LABEL[s]}</option>)}
            </select>
            {canEdit && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => { setLines([]); setForm({ supplierId: '', tenantId: '', entryMode: 'compra_ws', expectedAt: '', notes: '' }); setError(null); setOpen(true) }}
              >
                <Plus className="h-4 w-4" /> Pedido em branco
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {purchases.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum pedido de compra</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Pedido</th>
                    <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                    <th className="px-4 py-2.5 text-left font-medium">Dono</th>
                    <th className="px-4 py-2.5 text-right font-medium">Itens</th>
                    <th className="px-4 py-2.5 text-right font-medium">A receber</th>
                    <th className="px-4 py-2.5 text-right font-medium">Valor</th>
                    <th className="px-4 py-2.5 text-left font-medium">Previsão</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {purchases.map(p => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/industria/compras/${p.id}`)}
                      className="cursor-pointer hover:bg-blue-50/40"
                    >
                      <td className="px-4 py-2.5 font-mono font-medium text-gray-900">{p.number}</td>
                      <td className="px-4 py-2.5 text-gray-700">{p.supplierName}</td>
                      <td className="px-4 py-2.5 text-gray-600">{p.tenantName ?? 'WS (próprio)'}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{p.itemCount}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {p.pendingQty ? p.pendingQty.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">{fmtMoney(p.totalValue)}</td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {p.expectedAt ? (
                          <span className="inline-flex items-center gap-1">
                            <CalendarClock className="h-3 w-3 text-gray-400" /> {fmtDate(p.expectedAt)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={PURCHASE_STATUS_BADGE[p.status]}>{PURCHASE_STATUS_LABEL[p.status]}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal title="Novo pedido de compra" open={open} onClose={() => setOpen(false)} size="lg">
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); createMutation.mutate() }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Fornecedor</label>
              <select
                className={selectClass}
                value={form.supplierId}
                onChange={e => setForm({ ...form, supplierId: e.target.value })}
                required
              >
                <option value="">Selecione...</option>
                {suppliers.filter(s => s.active).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.leadTimeDays != null ? ` (${s.leadTimeDays} dias)` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Quem paga</label>
              <select
                className={selectClass}
                value={form.entryMode}
                onChange={e => setForm({ ...form, entryMode: e.target.value as 'compra_ws' | 'compra_por_conta' })}
              >
                <option value="compra_ws">A WS (material fica da casa)</option>
                <option value="compra_por_conta">Por conta do cliente (material nasce dele)</option>
              </select>
            </div>
            {form.entryMode === 'compra_por_conta' && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Marca dona</label>
                <select
                  className={selectClass}
                  value={form.tenantId}
                  onChange={e => setForm({ ...form, tenantId: e.target.value })}
                  required
                >
                  <option value="">Selecione...</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <Input
              label="Previsão de entrega"
              type="date"
              value={form.expectedAt}
              onChange={e => setForm({ ...form, expectedAt: e.target.value })}
            />
            <Input
              label="Observações"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Itens</span>
              <span className="text-sm text-gray-500">total {fmtMoney(total)}</span>
            </div>
            {lines.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                Selecione itens na necessidade líquida para trazê-los já com quantidade e custo.
              </p>
            ) : (
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={l.materialId} className="grid grid-cols-12 items-center gap-2">
                    <div className="col-span-6">
                      <span className="block truncate text-sm text-gray-900">{l.name}</span>
                      <span className="block font-mono text-xs text-gray-400">{l.code}</span>
                    </div>
                    <input
                      className="col-span-3 h-10 rounded-lg border border-gray-300 px-2 text-sm"
                      type="number"
                      min={1}
                      value={l.qty}
                      onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))}
                    />
                    <input
                      className="col-span-3 h-10 rounded-lg border border-gray-300 px-2 text-sm"
                      type="number"
                      step="0.0001"
                      min={0}
                      placeholder="custo un."
                      value={l.unitCost}
                      onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, unitCost: e.target.value } : x))}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Nasce como rascunho — enviar ao fornecedor é o passo seguinte.</span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!form.supplierId || lines.filter(l => Number(l.qty) > 0).length === 0}
              >
                Criar rascunho
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}
